import React, { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useTranslation } from "react-i18next";

const now = new Date();

const safe = (s) => String(s || "").replace(/[\\/"\r\n\t]/g, "").trim().replace(/\s+/g, "_") || "unknown";

export default function AdminReports() {
  const { t, i18n } = useTranslation();
  const [emps, setEmps] = useState([]);
  const [empId, setEmpId] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [reportType, setReportType] = useState("both");
  const [includeStatus, setIncludeStatus] = useState("approved");
  const [summaryYear, setSummaryYear] = useState(now.getFullYear());
  const [summaryMonth, setSummaryMonth] = useState(now.getMonth() + 1);
  const [summaryInclude, setSummaryInclude] = useState("approved");

  useEffect(() => { api.get("/employees").then((r) => setEmps(r.data)); }, []);

  const download = async (path, fname) => {
    // Item 5 bug fix: the previous version created an anchor but never
    // appended it to the DOM. On Firefox / mobile Safari that silently
    // no-ops the click. We now append + click + remove, and pass a proper
    // MIME type on the Blob so browsers recognise xlsx/pdf.
    try {
      const res = await api.get(path, { responseType: "blob" });
      const mime = res.headers?.["content-type"] || "application/octet-stream";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fname;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Delay revoke a tick so Safari doesn't cancel the download in flight.
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      // Surface backend errors instead of dying silently.
      // eslint-disable-next-line no-alert
      alert("ダウンロードに失敗しました: " + (e.response?.status || e.message));
    }
  };

  const lang = i18n.language === "en" ? "en" : "ja";
  const emp = emps.find((e) => e.id === empId);
  const monthStr = String(month).padStart(2, "0");
  const fnameBase = emp ? `${safe(emp.employee_code)}-${safe(emp.name)}-${year}${monthStr}` : `report-${year}${monthStr}`;

  return (
    <div>
      <h1 className="page-title">{t("admin.reports")}</h1>
      <div className="grid grid-2">
        <div className="card"><div className="card-body">
          <div className="card-title">{t("admin.perEmployee")}</div>
          <div className="field"><label className="label">{t("admin.name")}</label>
            <select className="select" value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="rep-emp">
              <option value="">{lang === "en" ? "Select" : "選択"}</option>
              {emps.map((e) => <option key={e.id} value={e.id}>{e.employee_code} - {e.name}</option>)}
            </select>
          </div>
          <div className="grid grid-2">
            <div className="field"><label className="label">{t("common.year")}</label>
              <input className="input" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
            </div>
            <div className="field"><label className="label">{t("common.month")}</label>
              <select className="select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{lang === "en" ? `${i+1}` : `${i+1}月`}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="label">{t("admin.reportType")}</label>
            <select className="select" value={reportType} onChange={(e) => setReportType(e.target.value)} data-testid="rep-type">
              <option value="both">{t("admin.reportBoth")}</option>
              <option value="attendance">{t("admin.reportAttendance")}</option>
              <option value="expense">{t("admin.reportExpense")}</option>
            </select>
          </div>
          <div className="field">
            <label className="label">{t("common.includeStatus")}</label>
            <select className="select" value={includeStatus} onChange={(e) => setIncludeStatus(e.target.value)} data-testid="rep-include-status">
              <option value="approved">{t("common.statusApprovedOnly")}</option>
              <option value="approved_submitted">{t("common.statusApprovedSubmitted")}</option>
            </select>
          </div>
          <div className="row-actions">
            <button className="btn btn-primary" disabled={!empId} onClick={() => download(`/reports/employee/${empId}/excel?year=${year}&month=${month}&report_type=${reportType}&lang=${lang}&include_status=${includeStatus}`, `${fnameBase}.xlsx`)} data-testid="rep-excel">Excel</button>
            <button className="btn btn-outline" disabled={!empId} onClick={() => download(`/reports/employee/${empId}/pdf?year=${year}&month=${month}&report_type=${reportType}&lang=${lang}&include_status=${includeStatus}`, `${fnameBase}.pdf`)} data-testid="rep-pdf">PDF</button>
          </div>
        </div></div>
        <div className="card"><div className="card-body">
          <div className="card-title">{t("admin.monthlySummary")}</div>
          <div className="grid grid-2">
            <div className="field"><label className="label">{t("common.year")}</label>
              <input className="input" type="number" value={summaryYear} onChange={(e) => setSummaryYear(Number(e.target.value))} />
            </div>
            <div className="field"><label className="label">{t("common.month")}</label>
              <select className="select" value={summaryMonth} onChange={(e) => setSummaryMonth(Number(e.target.value))}>
                {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{lang === "en" ? `${i+1}` : `${i+1}月`}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="label">{t("common.includeStatus")}</label>
            <select className="select" value={summaryInclude} onChange={(e) => setSummaryInclude(e.target.value)} data-testid="rep-summary-include-status">
              <option value="approved">{t("common.statusApprovedOnly")}</option>
              <option value="approved_submitted">{t("common.statusApprovedSubmitted")}</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={() => download(`/reports/summary/excel?year=${summaryYear}&month=${summaryMonth}&lang=${lang}&include_status=${summaryInclude}`, `summary-${summaryYear}${String(summaryMonth).padStart(2,"0")}.xlsx`)} data-testid="rep-summary">
            {lang === "en" ? "Summary Excel" : "サマリー Excel"}
          </button>
        </div></div>
      </div>
    </div>
  );
}
